import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { omit } from 'lodash'
import {
  assetsPayloadFromSearch,
  removeFilterValueFromExistingFilters,
} from '../../mappers/searchMapper'
import * as searchApi from '../apiService'
import {
  DEFAULT_ROWSPERPAGE,
  DEFAULT_SEARCH_TYPE,
  DEFAULT_SORT_BY_OPTION,
  searchDefaultFilters,
  SORT_BY_OPTIONS,
} from '../../constants/search'
// Map of active AbortControllers keyed by request id
const abortControllers = new Map()
const initialState = {
  triggerSearchCall: false,
  filters: searchDefaultFilters,
  sortFields: SORT_BY_OPTIONS[DEFAULT_SORT_BY_OPTION],
  searchType: DEFAULT_SEARCH_TYPE,
  searchTerm: '',
  aiSearchText: '',
  isExactMatch: false,
  page: Number(0),
  rowsPerPage: DEFAULT_ROWSPERPAGE,
  assetList: [],
  facets: {},
  channelFacets: {},
  classificationFacets: {},
  fileTypeFacets: {},
  pageLoading: false,
  totalResults: Number(0),
  selectedItems: [],
  templateId: '',
  previewImageRS: null,
  searchPayload: {},
  searchState: {},
  savedSearches: [],
  savedSearchesLoader: false,
  savedSearchError: {},
  savedSearchId: '',
  activeRequestId: null,
  isAISearchEnabled: false,
}

export const startOverSearch = () => async (dispatch) => {
  dispatch(searchSlice.actions.cancelSearchRequest())
  dispatch(searchSlice.actions.refreshSearch())
}

export const searchAssetsList = createAsyncThunk(
  'search/searchAssetsList',
  async (payload, { requestId }) => {
    abortControllers.forEach((controller, id) => {
      if (id !== requestId) {
        controller.abort()
        abortControllers.delete(id)
      }
    })
    const controller = new AbortController()
    abortControllers.set(requestId, controller)
    try {
      const response = await searchApi.searchAllAssets(payload, controller.signal)
      const { data: sampleResponse = {} } = response || {}
      abortControllers.delete(requestId)
      return sampleResponse
    } catch (error) {
      abortControllers.delete(requestId)
      if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return Promise.reject(new Error('aborted'))
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    }
  },
)

export const aiSearchAssetsList = createAsyncThunk(
  'search/aiSearchAssetsList',
  async (payload, { requestId }) => {
    abortControllers.forEach((controller, id) => {
      if (id !== requestId) {
        controller.abort()
        abortControllers.delete(id)
      }
    })
    const controller = new AbortController()
    abortControllers.set(requestId, controller)
    try {
      const response = await searchApi.aiSearchTextApi(payload, controller.signal)
      const { data: sampleResponse = {} } = response || {}
      abortControllers.delete(requestId)
      return sampleResponse
    } catch (error) {
      abortControllers.delete(requestId)
      if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return Promise.reject(new Error('aborted'))
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    }
  },
)

export const AITextNaturalSearch = createAsyncThunk(
  'search/aiSearchNaturalText',
  async (payloadProps, { requestId }) => {
    abortControllers.forEach((controller, id) => {
      if (id !== requestId) {
        controller.abort()
        abortControllers.delete(id)
      }
    })
    const controller = new AbortController()
    abortControllers.set(requestId, controller)
    try {
      const response = await searchApi.aiSearchNaturalTextApi(payloadProps)
      const { data: sampleResponse = {} } = response || {}
      abortControllers.delete(requestId)
      return sampleResponse
    } catch (error) {
      abortControllers.delete(requestId)
      if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return Promise.reject(new Error('aborted'))
      }
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      )
    }
  },
)

export const searchRSAssetsList = createAsyncThunk(
  'search/searchRSAssetsList',
  async (payload, { requestId }) => {
    abortControllers.forEach((controller, id) => {
      if (id !== requestId) {
        controller.abort()
        abortControllers.delete(id)
      }
    })
    const controller = new AbortController()
    abortControllers.set(requestId, controller)
    try {
      const response = await searchApi.searchAllReversAssetsByImage(payload, controller.signal)
      const { data: sampleResponse = {} } = response || {}
      abortControllers.delete(requestId)
      return sampleResponse
    } catch (error) {
      abortControllers.delete(requestId)
      if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return Promise.reject(new Error('aborted'))
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    }
  },
)

export const fetchSearchMetadata = createAsyncThunk(
  'search/fetchSearchMetadata',
  async () => {
    try {
      const response = await searchApi.fetchSearchMetadataApi()
      const { data: sampleResponse = {} } = response
      return sampleResponse
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    }
  },
)

export const fetchSavedSearches = createAsyncThunk(
  'search/fetchSavedSearches',
  async (searchTerm = '') => {
    try {
      const response = await searchApi.callSavedSearchesApi(searchTerm)
      const { data: savedSearches = {} } = response
      const { content = [] } = savedSearches || {}
      return content
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    }
  },
)

export const createNewSavedSearch = createAsyncThunk(
  'search/createNewSavedSearch',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await searchApi.createNewSavedSearchApi(payload)
      const { data: savedSearch = {} } = response
      return savedSearch
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // Pass backend error to rejected action
        return rejectWithValue(error.response.data)
      }
      return rejectWithValue({ error_message: 'Unknown error occurred' })
    }
  },
)

export const updateSavedSearchById = createAsyncThunk(
  'search/updateSavedSearchById',
  async (fullPayloadObj, { rejectWithValue }) => {
    try {
      const response = await searchApi.updateSavedSearchByIdApi(fullPayloadObj)
      const { data: updatedSearch = {} } = response
      return updatedSearch
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // Pass backend error to rejected action
        return rejectWithValue(error.response.data)
      }
      return rejectWithValue({ error_message: 'Unknown error occurred' })
    }
  },
)

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    resetFilters: () => ({ ...initialState }),
    cancelSearchRequest: (state) => {
      abortControllers.forEach((controller) => controller.abort())
      abortControllers.clear()
      state.pageLoading = false
    },
    resetSavedSearchError: (state, action) => {
      state.savedSearchError = null
    },
    enableAssetLoader: (state) => {
      state.pageLoading = true
    },
    disableAssetLoader: (state) => {
      state.pageLoading = false
    },
    updateSearchTerm: (state, action) => {
      state.searchTerm = action.payload
      state.templateId = ''
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    updateSearchTermWithType: (state, action) => {
      state.searchTerm = action.payload.searchTerm
      state.searchType = action.payload.searchType
      state.isExactMatch = action.payload.isExactMatch
      state.templateId = ''
      state.savedSearchId = ''
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    updateSearchPayload: (state, action) => {
      state.filters = { ...state.filters, ...action.payload.filters }
      state.templateId = ''
      state.savedSearchId = ''
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    removeFilterValue: (state, action) => {
      const { filterToRemove, channelConfig = {} } = action.payload
      const stateFilters = { ...state.filters }
      const newFilters = removeFilterValueFromExistingFilters(
        stateFilters,
        filterToRemove,
        channelConfig,
      )
      state.filters = newFilters
      state.templateId = ''
      state.savedSearchId = ''
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    removeFilterFromPayload: (state, action) => {
      const filtersToRemove = action.payload
      const stateFilters = { ...state.filters }
      const newFilters = omit(stateFilters, filtersToRemove)
      state.filters = newFilters
      state.templateId = ''
      state.savedSearchId = ''
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    replaceFilters: (state, action) => {
      state.filters = action.payload.filters
      state.templateId = ''
      state.savedSearchId = ''
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    removeSearchTerm: (state, action) => {
      state.searchTerm = ''
      state.searchType = DEFAULT_SEARCH_TYPE
      state.templateId = ''
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    updateSortFields: (state, action) => {
      state.sortFields = action.payload
      state.savedSearchId = ''
      state.triggerSearchCall = !state.triggerSearchCall
    },
    updatePageNumber: (state, action) => {
      state.page = action.payload
      state.savedSearchId = ''
      state.triggerSearchCall = !state.triggerSearchCall
    },
    updateRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload
      state.page = Number(0)
      state.savedSearchId = ''
      state.triggerSearchCall = !state.triggerSearchCall
    },
    refreshSearch: (state, action) => {
      state.filters = searchDefaultFilters
      state.templateId = ''
      state.page = Number(0)
      state.rowsPerPage = DEFAULT_ROWSPERPAGE
      state.searchTerm = ''
      state.searchType = DEFAULT_SEARCH_TYPE
      state.isExactMatch = false
      state.sortFields = SORT_BY_OPTIONS[DEFAULT_SORT_BY_OPTION]
      const { skipAutoTrigger = false } = action?.payload || {}
      if (!skipAutoTrigger) {
        state.triggerSearchCall = !state.triggerSearchCall
      }
      state.selectedItems = []
      state.savedSearchId = ''
      abortControllers.forEach((controller) => controller.abort())
      abortControllers.clear()
    },
    updateUserSavedFilters: (state, action) => {
      state.filters = action.payload.search_preference.filters
      state.page = action.payload.search_preference.page
      state.rowsPerPage = action.payload.search_preference.rows_per_page
      state.searchTerm = action.payload.search_preference.search_term
      state.searchType = action.payload?.search_preference?.search_type || DEFAULT_SEARCH_TYPE
      state.sortFields = action.payload.search_preference.sort_fields
      state.templateId = action.payload.search_preference.template_id
      state.isExactMatch = action.payload.search_preference.is_exact_match
      state.savedSearchId = action.payload.saved_search_id
      state.triggerSearchCall = !state.triggerSearchCall
    },
    updateSavedSearchId: (state, action) => {
      state.savedSearchId = action.payload
      state.triggerSearchCall = !state.triggerSearchCall
    },
    enableTriggerSearchCall: (state) => {
      state.triggerSearchCall = !state.triggerSearchCall
    },
    clearAllAppliedFilters: (state, action) => {
      state.filters = searchDefaultFilters
      state.triggerSearchCall = !state.triggerSearchCall
      state.templateId = ''
      state.savedSearchId = ''
      state.page = Number(0)
    },
    saveSelectedItems: (state, action) => {
      state.selectedItems = action?.payload?.selectedItems
    },
    saveSearchByTextPayload: (state, action) => {
      state.searchPayload = action?.payload?.searchPayload
    },
    saveSearchPersistState: (state, action) => {
      state.searchState = action?.payload?.searchState
    },
    savePreviewImage: (state, action) => {
      state.previewImageRS = action?.payload?.previewImageRS
    },
    saveTemplateId: (state, action) => {
      state.templateId = action?.payload
      state.searchTerm = ''
      state.searchType = DEFAULT_SEARCH_TYPE
      state.isExactMatch = false
      state.page = Number(0)
      state.triggerSearchCall = !state.triggerSearchCall
    },
    toggleAISearch: (state, action) => {
      state.isAISearchEnabled = action.payload
      state.page = Number(0)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchAssetsList.pending, (state, action) => {
      state.activeRequestId = action.meta.requestId
      state.pageLoading = true
    })
    builder.addCase(searchAssetsList.fulfilled, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const {
        payload: {
          filters = {},
          facets = {},
          search = [],
          total_results = Number(1),
          search_term: response_search_term = '',
        } = {},
        meta: {
          arg: {
            page = Number(0),
            rowsPerPage = DEFAULT_ROWSPERPAGE,
            payload: { search_term = '' },
          } = {},
        } = {},
      } = action
      state.searchTerm = response_search_term || search_term
      const searchList = assetsPayloadFromSearch(search)
      state.filters = filters
      state.page = page
      state.rowsPerPage = rowsPerPage
      state.pageLoading = false
      state.facets = facets
      state.assetList = searchList
      state.totalResults = total_results
      state.activeRequestId = null
    })
    builder.addCase(searchAssetsList.rejected, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const aborted =
        action.error?.name === 'AbortError' ||
        action.error?.name === 'CanceledError' ||
        action.error?.aborted
      if (!aborted) {
        state.totalResults = 0
        state.assetList = []
      }
      state.pageLoading = false
      state.activeRequestId = null
    })
    builder.addCase(searchRSAssetsList.pending, (state, action) => {
      state.activeRequestId = action.meta.requestId
      state.pageLoading = true
    })
    builder.addCase(searchRSAssetsList.fulfilled, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const { payload: { search = [], total_results = Number(1) } = {} } =
        action
      const searchList = assetsPayloadFromSearch(search)
      state.pageLoading = false
      state.assetList = searchList
      state.totalResults = total_results
      state.selectedItems = []
      state.activeRequestId = null
    })
    builder.addCase(searchRSAssetsList.rejected, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const aborted =
        action.error?.name === 'AbortError' ||
        action.error?.name === 'CanceledError' ||
        action.error?.aborted
      if (!aborted) {
        state.selectedItems = []
      }
      state.pageLoading = false
      state.activeRequestId = null
    })
    builder.addCase(fetchSavedSearches.fulfilled, (state, action) => {
      state.savedSearches = action?.payload
      state.savedSearchesLoader = false
    })
    builder.addCase(fetchSavedSearches.pending, (state, action) => {
      state.savedSearchesLoader = true
    })
    builder.addCase(fetchSavedSearches.rejected, (state, action) => {
      state.savedSearches = []
      state.savedSearchesLoader = false
    })
    builder.addCase(createNewSavedSearch.fulfilled, (state, action) => {
      const newlyAddedSearch = action?.payload || {}
      state.savedSearches = [...state.savedSearches, newlyAddedSearch]
      state.savedSearchError = null
    })
    builder.addCase(createNewSavedSearch.rejected, (state, action) => {
      state.savedSearchError = action.payload
    })
    builder.addCase(updateSavedSearchById.fulfilled, (state, action) => {
      const { saved_search_id = '' } = action.meta.arg || {}
      const updatedSearch = action?.payload || {}
      state.savedSearches = state.savedSearches.map((search) =>
        search.saved_search_id === saved_search_id ? updatedSearch : search,
      )
      state.savedSearchError = null
    })
    builder.addCase(updateSavedSearchById.rejected, (state, action) => {
      state.savedSearchError = action.payload
    })
    builder.addCase(fetchSearchMetadata.fulfilled, (state, action) => {
      const { payload = {} } = action
      const channelRecords = payload['channel'] || []
      state.channelFacets = {
        key: 'Channel',
        path: 'metadata.projectInfo.channel',
        records: channelRecords,
      }
      const classificationRecords = payload['classification'] || []
      state.classificationFacets = {
        key: 'Classification',
        path: 'metadata.assetClassification.classificationId',
        records: classificationRecords,
      }
      const fileTypeRecords = payload['file_type'] || []
      state.fileTypeFacets = {
        key: 'File Type',
        path: 'metadata.file_type',
        records: fileTypeRecords,
      }
    })
    builder.addCase(aiSearchAssetsList.pending, (state, action) => {
      state.activeRequestId = action.meta.requestId
      state.pageLoading = true
    })
    builder.addCase(aiSearchAssetsList.fulfilled, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const {
        payload: {
          filters = {},
          facets = {},
          search = [],
          total_results = Number(1),
        } = {},
      } = action
      const searchList = assetsPayloadFromSearch(search)
      state.filters = filters
      state.facets = facets
      state.assetList = searchList
      state.totalResults = total_results
      state.pageLoading = false
      state.activeRequestId = null
    })
    builder.addCase(aiSearchAssetsList.rejected, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const aborted =
        action.error?.name === 'AbortError' ||
        action.error?.name === 'CanceledError' ||
        action.error?.aborted
      if (!aborted) {
        state.totalResults = 0
        state.assetList = []
      }
      state.pageLoading = false
      state.activeRequestId = null
    })
    builder.addCase(AITextNaturalSearch.pending, (state, action) => {
      state.activeRequestId = action.meta.requestId
      state.pageLoading = true
    })
    builder.addCase(AITextNaturalSearch.fulfilled, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const {
        payload: {
          filters = {},
          facets = {},
          search = [],
          total_results = Number(1),
        } = {},
      } = action
      const searchList = assetsPayloadFromSearch(search)
      state.filters = filters
      state.facets = facets
      state.assetList = searchList
      state.totalResults = total_results
      state.pageLoading = false
      state.activeRequestId = null
    })
    builder.addCase(AITextNaturalSearch.rejected, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return
      const aborted =
        action.error?.name === 'AbortError' ||
        action.error?.name === 'CanceledError' ||
        action.error?.aborted
      if (!aborted) {
        state.totalResults = 0
        state.assetList = []
      }
      state.pageLoading = false
      state.activeRequestId = null
    })
  },
})

export default searchSlice.reducer
export const {
  updateSearchPayload,
  updateSearchTerm,
  updateSearchTermWithType,
  removeFilterFromPayload,
  replaceFilters,
  removeFilterValue,
  removeSearchTerm,
  clearAllAppliedFilters,
  saveSelectedItems,
  saveTemplateId,
  saveSearchByTextPayload,
  saveSearchPersistState,
  resetFilters,
  updateSortFields,
  updatePageNumber,
  updateRowsPerPage,
  refreshSearch,
  enableAssetLoader,
  disableAssetLoader,
  enableTriggerSearchCall,
  updateUserSavedFilters,
  updateSavedSearchId,
  resetSavedSearchError,
  cancelSearchRequest,
  toggleAISearch,
} = searchSlice.actions